import { apiFetch } from '@/lib/api/client'
import { mapCatalogCategory, mapCatalogModel } from '@/lib/api/mappers'
import type { ScoreHistoryPoint } from '@/components/catalog/ScoreHistoryChart'
import type { CatalogCategory, CatalogFilters, CatalogModel } from '@/types/catalog'
import { MOCK_CATEGORIES, MOCK_MODELS } from '@/lib/catalog/mock-data'
import { getMockUserScoreOverride } from '@/lib/catalog/mock-user-score'
import { filterModels } from '@/lib/catalog/filters'
import { useMockApi } from '@/lib/api/client'

export interface ModelsListQuery {
  categoryId?: string
  filters?: CatalogFilters
  page?: number
  perPage?: number
}

export interface ModelEnrichment {
  fetchedAt?: string
  huggingFace?: {
    modelId: string
    downloads?: number
    likes?: number
    pipelineTag?: string
    libraryName?: string
    tags: string[]
    sourceUrl: string
  }
  artificialAnalysis?: {
    slug?: string
    name?: string
    intelligenceIndex?: number
    medianOutputTokensPerSecond?: number
    medianTimeToFirstTokenSeconds?: number
    inputPricePerMillion?: number
    outputPricePerMillion?: number
    sourceUrl: string
  }
}

export interface ModelsApi {
  listCategories(): Promise<CatalogCategory[]>
  listModels(query: ModelsListQuery): Promise<{ items: CatalogModel[]; total: number }>
  archiveModel(modelId: string, categoryId: string): Promise<void>
  restoreModel(modelId: string, categoryId: string): Promise<void>
  setModelPinned(modelId: string, categoryId: string, pinned: boolean): Promise<void>
  hideModel(modelId: string, categoryId: string): Promise<void>
  blacklistModel(modelId: string, categoryId: string): Promise<void>
  getScoreHistory(modelId: string, categoryId?: string): Promise<ScoreHistoryPoint[]>
  getEnrichment(modelId: string): Promise<ModelEnrichment | null>
  enrichModel(modelId: string): Promise<ModelEnrichment>
}

class MockModelsApi implements ModelsApi {
  private findModel(modelId: string, categoryId: string) {
    return MOCK_MODELS.find((item) => item.id === modelId && item.categoryId === categoryId)
  }

  private withUserScoreOverrides(models: CatalogModel[]): CatalogModel[] {
    return models.map((model) => {
      const override = getMockUserScoreOverride(model.id, model.categoryId)
      if (override === undefined) return model
      return { ...model, userScore: override }
    })
  }

  async listCategories(): Promise<CatalogCategory[]> {
    return MOCK_CATEGORIES
  }

  async listModels(query: ModelsListQuery): Promise<{ items: CatalogModel[]; total: number }> {
    const categoryId = query.categoryId ?? MOCK_CATEGORIES[0]?.id ?? null
    const filters = query.filters ?? { status: 'active', freeOnly: false, maxPrice: null, providers: [] }
    const items = this.withUserScoreOverrides(filterModels(MOCK_MODELS, categoryId, filters))
    return { items, total: items.length }
  }

  async archiveModel(modelId: string, categoryId: string): Promise<void> {
    const model = this.findModel(modelId, categoryId)
    if (model) model.status = 'archived'
  }

  async restoreModel(modelId: string, categoryId: string): Promise<void> {
    const model = this.findModel(modelId, categoryId)
    if (model) model.status = 'active'
  }

  async setModelPinned(modelId: string, categoryId: string, pinned: boolean): Promise<void> {
    const model = this.findModel(modelId, categoryId)
    if (model) model.isPinned = pinned
  }

  async hideModel(modelId: string, categoryId: string): Promise<void> {
    const model = this.findModel(modelId, categoryId)
    if (model) model.status = 'hidden'
  }

  async blacklistModel(modelId: string, categoryId: string): Promise<void> {
    const model = this.findModel(modelId, categoryId)
    if (model) {
      model.status = 'blacklisted'
      model.isPinned = false
    }
  }

  async getScoreHistory(modelId: string, categoryId?: string): Promise<ScoreHistoryPoint[]> {
    return [
      {
        id: 'history-1',
        categoryId: categoryId ?? MOCK_CATEGORIES[0]?.id ?? 'cat-1',
        categoryName: 'Coding',
        oldScore: 0.62,
        newScore: 0.71,
        reason: 'user_rating',
        createdAt: new Date().toISOString(),
      },
    ]
  }

  async getEnrichment(modelId: string): Promise<ModelEnrichment | null> {
    return {
      fetchedAt: new Date().toISOString(),
      huggingFace: {
        modelId: 'meta-llama/Llama-3-8B-Instruct',
        downloads: 1200000,
        likes: 4200,
        pipelineTag: 'text-generation',
        libraryName: 'transformers',
        tags: ['llama', 'instruct'],
        sourceUrl: 'https://huggingface.co/api/models/meta-llama/Llama-3-8B-Instruct',
      },
    }
  }

  async enrichModel(modelId: string): Promise<ModelEnrichment> {
    return (await this.getEnrichment(modelId)) ?? { fetchedAt: new Date().toISOString() }
  }
}

class HttpModelsApi implements ModelsApi {
  async listCategories(): Promise<CatalogCategory[]> {
    const body = await apiFetch<{ items: unknown[] }>('/api/v1/categories')
    return body.items.map(mapCatalogCategory)
  }

  async listModels(query: ModelsListQuery): Promise<{ items: CatalogModel[]; total: number }> {
    const params = new URLSearchParams()
    if (query.categoryId) params.set('category_id', query.categoryId)

    const filters = query.filters
    if (filters) {
      if (filters.status === 'pinned') {
        params.set('pinned', 'true')
        params.set('status', 'active')
      } else if (filters.status !== 'all') {
        params.set('status', filters.status)
      }

      if (filters.freeOnly) params.set('free_only', 'true')
      if (filters.maxPrice !== null) params.set('max_input_price', String(filters.maxPrice))
      if (filters.providers.length === 1) {
        params.set('provider_id', filters.providers[0])
      }
    }

    if (query.page) params.set('page', String(query.page))
    if (query.perPage) params.set('per_page', String(query.perPage))

    const body = await apiFetch<{ items: unknown[]; total: number }>(
      `/api/v1/models?${params.toString()}`,
    )

    let items = body.items.map(mapCatalogModel)

    if (filters && filters.providers.length > 1) {
      items = items.filter((model) => filters.providers.includes(model.providerId))
    }

    return { items, total: body.total }
  }

  async archiveModel(modelId: string, categoryId: string): Promise<void> {
    await apiFetch(`/api/v1/models/${modelId}/categories/${categoryId}/archive`, {
      method: 'POST',
      body: JSON.stringify({ reason: 'manually_archived' }),
    })
  }

  async restoreModel(modelId: string, categoryId: string): Promise<void> {
    await apiFetch(`/api/v1/models/${modelId}/categories/${categoryId}/restore`, {
      method: 'POST',
    })
  }

  async setModelPinned(modelId: string, categoryId: string, pinned: boolean): Promise<void> {
    await apiFetch(`/api/v1/models/${modelId}/categories/${categoryId}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_pinned: pinned }),
    })
  }

  async hideModel(modelId: string, categoryId: string): Promise<void> {
    await apiFetch(`/api/v1/models/${modelId}/categories/${categoryId}/hide`, {
      method: 'POST',
    })
  }

  async blacklistModel(modelId: string, categoryId: string): Promise<void> {
    await apiFetch(`/api/v1/models/${modelId}/categories/${categoryId}/blacklist`, {
      method: 'POST',
    })
  }

  async getScoreHistory(modelId: string, categoryId?: string): Promise<ScoreHistoryPoint[]> {
    const params = new URLSearchParams()
    if (categoryId) params.set('category_id', categoryId)
    params.set('limit', '20')

    const body = await apiFetch<{ items: Array<Record<string, unknown>> }>(
      `/api/v1/models/${modelId}/score-history?${params.toString()}`,
    )

    return body.items.map((item) => ({
      id: String(item.id ?? ''),
      categoryId: String(item.category_id ?? ''),
      categoryName: String(item.category_name ?? ''),
      oldScore: item.old_score == null ? null : Number(item.old_score),
      newScore: Number(item.new_score ?? 0),
      reason: String(item.reason ?? ''),
      createdAt: String(item.created_at ?? ''),
    }))
  }

  async getEnrichment(modelId: string): Promise<ModelEnrichment | null> {
    try {
      const body = await apiFetch<Record<string, unknown>>(`/api/v1/models/${modelId}/enrichment`)
      return mapModelEnrichment(body)
    } catch {
      return null
    }
  }

  async enrichModel(modelId: string): Promise<ModelEnrichment> {
    const body = await apiFetch<Record<string, unknown>>(`/api/v1/models/${modelId}/enrich`, {
      method: 'POST',
    })
    return mapModelEnrichment(body) ?? { fetchedAt: new Date().toISOString() }
  }
}

function mapModelEnrichment(body: Record<string, unknown>): ModelEnrichment | null {
  const huggingFace = body.hugging_face as Record<string, unknown> | undefined
  const artificialAnalysis = body.artificial_analysis as Record<string, unknown> | undefined

  if (!body.fetched_at && !huggingFace && !artificialAnalysis) return null

  return {
    fetchedAt: body.fetched_at ? String(body.fetched_at) : undefined,
    huggingFace: huggingFace
      ? {
          modelId: String(huggingFace.model_id ?? ''),
          downloads: huggingFace.downloads == null ? undefined : Number(huggingFace.downloads),
          likes: huggingFace.likes == null ? undefined : Number(huggingFace.likes),
          pipelineTag: huggingFace.pipeline_tag ? String(huggingFace.pipeline_tag) : undefined,
          libraryName: huggingFace.library_name ? String(huggingFace.library_name) : undefined,
          tags: Array.isArray(huggingFace.tags) ? huggingFace.tags.map(String) : [],
          sourceUrl: String(huggingFace.source_url ?? ''),
        }
      : undefined,
    artificialAnalysis: artificialAnalysis
      ? {
          slug: artificialAnalysis.slug ? String(artificialAnalysis.slug) : undefined,
          name: artificialAnalysis.name ? String(artificialAnalysis.name) : undefined,
          intelligenceIndex:
            artificialAnalysis.intelligence_index == null
              ? undefined
              : Number(artificialAnalysis.intelligence_index),
          medianOutputTokensPerSecond:
            artificialAnalysis.median_output_tokens_per_second == null
              ? undefined
              : Number(artificialAnalysis.median_output_tokens_per_second),
          medianTimeToFirstTokenSeconds:
            artificialAnalysis.median_time_to_first_token_seconds == null
              ? undefined
              : Number(artificialAnalysis.median_time_to_first_token_seconds),
          inputPricePerMillion:
            artificialAnalysis.input_price_per_million == null
              ? undefined
              : Number(artificialAnalysis.input_price_per_million),
          outputPricePerMillion:
            artificialAnalysis.output_price_per_million == null
              ? undefined
              : Number(artificialAnalysis.output_price_per_million),
          sourceUrl: String(artificialAnalysis.source_url ?? ''),
        }
      : undefined,
  }
}

export const modelsApi: ModelsApi = useMockApi() ? new MockModelsApi() : new HttpModelsApi()
